import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { ChevronLeft, ShieldCheck, Truck, ArrowRight, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHIPPING_FLAT = 0;
const FREE_SHIPPING_THRESHOLD = 0;
const TAX_RATE = 0.07;

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplying, setPromoApplying] = useState(false);
  const [promo, setPromo] = useState(null); // { code, amount }
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    shipping_address: "", shipping_city: "", shipping_state: "", shipping_zip: "", shipping_country: "United States",
    notes: "",
  });

  const shipping = 0;
  const tax = +(subtotal * TAX_RATE).toFixed(2);
  const discount = promo?.amount || 0;
  const total = +Math.max(0, subtotal + shipping + tax - discount).toFixed(2);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const applyPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoApplying(true);
    try {
      const { data } = await axios.get(`${API}/promo/validate`, { params: { code, subtotal: +subtotal.toFixed(2) } });
      setPromo({ code: data.code, amount: data.amount });
      toast({ title: `Code applied: $${data.amount.toFixed(2)} off`, description: data.code });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid code";
      setPromo(null);
      toast({ title: "Code not applied", description: String(msg), variant: "destructive" });
    } finally {
      setPromoApplying(false);
    }
  };

  const removePromo = () => { setPromo(null); setPromoCode(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const orderItems = items.map((it) => ({
        slug: it.slug,
        title: it.title,
        price: parseFloat(String(it.price).replace(/[^0-9.]/g, "")),
        qty: it.qty,
        size: it.size,
        color: it.color,
        color_name: it.colorName || null,
        customization: it.customization || null,
        img: it.img,
        design_option: it.designOption || null,
        design_file_id: it.designFileId || null,
        design_file_name: it.designFileName || null,
        design_notes: it.designNotes || null,
      }));
      const payload = { ...form, items: orderItems, subtotal: +subtotal.toFixed(2), shipping, tax, total,
                        discount_code: promo?.code || null, discount_amount: discount,
                        origin_url: window.location.origin };
      const { data } = await axios.post(`${API}/payments/checkout`, payload);
      // Clear cart and go to the order confirmation page (which shows Zelle payment instructions)
      clear();
      navigate(data.redirect_url || `/order/${data.order_number}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast({ title: "Could not place order", description: String(detail), variant: "destructive" });
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[color:var(--dms-cream)]">
        <Navbar />
        <div className="max-w-3xl mx-auto py-24 px-5 text-center">
          <h1 className="font-serif text-4xl text-[color:var(--dms-navy)]">Your cart is empty</h1>
          <p className="mt-4 text-[color:var(--dms-navy)]/60">Add a shirt and come back here to check out.</p>
          <Link to="/t-shirts" className="btn-peach mt-8 inline-flex">View more <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--dms-cream)]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-8">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[color:var(--dms-navy)]/70 hover:text-[color:var(--dms-navy)]"><ChevronLeft className="w-4 h-4" /> Continue shopping</Link>
      </div>
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <h1 className="font-serif text-4xl md:text-5xl text-[color:var(--dms-navy)]">Checkout</h1>
        <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mt-4" />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <form onSubmit={submit} className="lg:col-span-3 space-y-8">
            <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6 md:p-8">
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Contact</h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name" required><input required value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} className={inputCls} /></Field>
                <Field label="Email" required><input required type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} className={inputCls} /></Field>
                <Field label="Phone"><input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6 md:p-8">
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Shipping address</h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Street address" required className="sm:col-span-2"><input required value={form.shipping_address} onChange={(e) => update("shipping_address", e.target.value)} className={inputCls} /></Field>
                <Field label="City" required><input required value={form.shipping_city} onChange={(e) => update("shipping_city", e.target.value)} className={inputCls} /></Field>
                <Field label="State" required><input required value={form.shipping_state} onChange={(e) => update("shipping_state", e.target.value)} className={inputCls} /></Field>
                <Field label="ZIP" required><input required value={form.shipping_zip} onChange={(e) => update("shipping_zip", e.target.value)} className={inputCls} /></Field>
                <Field label="Country" required><input required value={form.shipping_country} onChange={(e) => update("shipping_country", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6 md:p-8">
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Notes for our team</h2>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/60">Include any special instructions, deadlines, or design notes.</p>
              <textarea rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Optional…" className={`${inputCls} mt-4`} />
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-[color:var(--dms-navy)]/60 max-w-sm">We'll email you an invoice. Pay by Zelle to <b>designmystoryus@gmail.com</b> using your order # as the memo.</p>
              <button type="submit" disabled={submitting} className="btn-peach disabled:opacity-60">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending invoice…</> : <>Place order &amp; get invoice <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>

          <aside className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6 md:p-8 sticky top-24">
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Order summary</h2>
              <ul className="mt-5 space-y-4 max-h-80 overflow-y-auto pr-1">
                {items.map((it) => (
                  <li key={it.key} className="flex gap-3">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-[color:var(--dms-peach-light)]/40 flex-shrink-0">
                      <img src={it.img} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-sm text-[color:var(--dms-navy)] leading-snug line-clamp-2">{it.title}</p>
                      <p className="text-[11px] text-[color:var(--dms-navy)]/60 mt-1"><span className="inline-block w-2.5 h-2.5 rounded-full mr-1 align-middle border border-black/10" style={{ background: it.color }} />{it.colorName} · {it.size} · x{it.qty}</p>
                    </div>
                    <p className="font-serif text-sm text-[color:var(--dms-navy)]">{it.price}</p>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-[color:var(--dms-navy)]/10 space-y-2 text-sm text-[color:var(--dms-navy)]">
                <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                <Row label="Shipping" value="Free" />
                <Row label="Tax (est.)" value={`$${tax.toFixed(2)}`} />
                {promo && <Row label={`Discount (${promo.code})`} value={`− $${discount.toFixed(2)}`} />}
              </div>
              {/* Promo code */}
              <div className="mt-5">
                {promo ? (
                  <div className="flex items-center justify-between bg-[color:var(--dms-peach-light)]/40 border border-[color:var(--dms-peach)]/40 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-peach)] font-semibold">Code applied</p>
                      <p className="font-serif text-[color:var(--dms-navy)]">{promo.code} · −${discount.toFixed(2)}</p>
                    </div>
                    <button onClick={removePromo} className="text-xs tracking-widest uppercase font-semibold text-[color:var(--dms-navy)]/60 hover:text-[color:var(--dms-navy)]">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input value={promoCode} onChange={(e) => setPromoCode(e.target.value)} placeholder="Promo code"
                      className="flex-1 rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--dms-peach)]" />
                    <button type="button" onClick={applyPromo} disabled={promoApplying || !promoCode.trim()}
                      className="btn-ghost-navy disabled:opacity-50 !py-2.5 !px-5 text-[11px]">
                      {promoApplying ? "…" : "Apply"}
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t-2 border-[color:var(--dms-navy)] flex items-center justify-between text-[color:var(--dms-navy)]">
                <span className="text-sm tracking-widest uppercase font-semibold">Total</span>
                <span className="font-serif text-3xl">${total.toFixed(2)}</span>
              </div>
              <div className="mt-6 space-y-2 text-xs text-[color:var(--dms-navy)]/60">
                <p className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[color:var(--dms-peach)]" /> Proof approval before every order</p>
                <p className="flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-[color:var(--dms-peach)]" /> Free shipping on every order</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 text-[color:var(--dms-navy)] focus:outline-none focus:border-[color:var(--dms-peach)] transition";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}{required && <span className="text-[color:var(--dms-peach)]"> *</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[color:var(--dms-navy)]/70">{label}</span>
      <span>{value}</span>
    </div>
  );
}
